/**
 * Beobachteter Verbrauch (27.10.2026): „Milch kauft ihr etwa alle 4 Tage —
 * zuletzt vor 5 Tagen.“
 *
 * Gerechnet wird **aus dem, was ohnehin da ist**: Ein abgehakter Eintrag der
 * Einkaufsliste ist ein Kauf (`checkedAt`), auch nachdem „Einkauf
 * abschließen“ ihn weich gelöscht hat. Es gibt keine eigene Tabelle — und
 * weil der Einkauf Ende-zu-Ende verschlüsselt ist, kann nur das Gerät diese
 * Rechnung machen; der Server sieht keine Namen.
 *
 * Ehrlich statt geraten:
 * - unter drei Käufen gibt es kein Muster (`insufficient`), nur die Zahl;
 * - das Intervall ist der **Median** der Abstände (ein Urlaub verschiebt ihn
 *   nicht), gezählt in Kalendertagen der Nutzerzeitzone, nie in UTC;
 * - streuen die Abstände zu stark, heißt es `irregular` und wird nicht
 *   vorgeschlagen;
 * - gleich ist nur, was nach `itemKey` gleich heißt — kein unscharfes Raten
 *   zwischen „Milch“ und „Hafermilch“.
 */

import { itemKey } from './shopping.js';
import type { ShelfCategory } from './shopping.js';
import { isoDateInZone } from './time.js';
import type { IsoDate } from './time.js';

/** Ab so vielen Käufen (an verschiedenen Tagen) gibt es ein Muster. */
export const MIN_PURCHASES = 3;
/** Nur die jüngsten Abstände zählen — Gewohnheiten ändern sich. */
export const MAX_INTERVALS = 8;
/** Streuung (mittlere absolute Abweichung vom Median, relativ) bis hierher ist ein Muster. */
export const MAX_SPREAD = 0.5;

export type ConsumptionStatus =
  /** Weniger als `MIN_PURCHASES` Käufe — noch zu wenig beobachtet. */
  | 'insufficient'
  /** Die Abstände streuen zu stark für eine Aussage. */
  | 'irregular'
  /** Seit vielen Intervallen nicht mehr gekauft — wohl nicht mehr gebraucht. */
  | 'lapsed'
  | 'pattern';

export type ConsumptionConfidence = 'low' | 'medium' | 'high';

export interface ConsumptionPattern {
  status: ConsumptionStatus;
  /** Käufe an verschiedenen Tagen. */
  purchases: number;
  /** Median der Abstände in Tagen; `null` ohne mindestens einen Abstand. */
  intervalDays: number | null;
  lastBought: IsoDate | null;
  /** Tage seit dem letzten Kauf; `null` ohne Kauf. */
  daysSince: number | null;
  /** Bald wieder dran — nur bei `pattern`. */
  due: boolean;
  /** Nur bei `pattern`. */
  confidence: ConsumptionConfidence | null;
}

function dayNumber(date: IsoDate): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Kalendertage von `from` bis `to` (reine Datumsarithmetik). */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return dayNumber(to) - dayNumber(from);
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Das Muster aus den Kauftagen. Mehrfaches am selben Tag zählt einmal, Tage
 * nach `today` (Uhr eines anderen Geräts) gar nicht.
 */
export function consumptionPattern(days: readonly IsoDate[], today: IsoDate): ConsumptionPattern {
  const unique = [...new Set(days)].filter((day) => day <= today).sort();
  const lastBought = unique.at(-1) ?? null;
  const daysSince = lastBought ? daysBetween(lastBought, today) : null;
  const base = { purchases: unique.length, lastBought, daysSince };

  const recent = unique.slice(-(MAX_INTERVALS + 1));
  const gaps = recent.slice(1).map((day, index) => daysBetween(recent[index]!, day));
  const intervalDays = gaps.length > 0 ? Math.max(1, Math.round(median(gaps))) : null;

  if (unique.length < MIN_PURCHASES || intervalDays === null || daysSince === null) {
    return { ...base, status: 'insufficient', intervalDays, due: false, confidence: null };
  }

  const center = median(gaps);
  const spread = median(gaps.map((gap) => Math.abs(gap - center))) / center;
  if (spread > MAX_SPREAD) {
    return { ...base, status: 'irregular', intervalDays, due: false, confidence: null };
  }

  // Nach einem Urlaub ist es trotzdem dran; nach Monaten wohl nicht mehr.
  if (daysSince > Math.max(3 * intervalDays, intervalDays + 14)) {
    return { ...base, status: 'lapsed', intervalDays, due: false, confidence: null };
  }

  const confidence: ConsumptionConfidence =
    gaps.length <= 2 ? 'low' : gaps.length >= 5 && spread <= 0.25 ? 'high' : 'medium';
  // Einen Tag Vorlauf, damit es auf der Liste steht, bevor es fehlt — aber
  // nie am Tag des Kaufs selbst.
  const due = daysSince >= Math.max(1, intervalDays - 1);
  return { ...base, status: 'pattern', intervalDays, due, confidence };
}

/** Ein Eintrag der Einkaufsliste, wie er lokal liegt — auch weich gelöscht. */
export interface PurchaseRow {
  name: string;
  category: ShelfCategory;
  checkedAt: string | null;
  deletedAt?: string | null;
  updatedAt: string;
}

export interface ConsumptionSuggestion {
  key: string;
  /** So, wie es zuletzt gekauft wurde. */
  name: string;
  category: ShelfCategory;
  pattern: ConsumptionPattern;
}

/**
 * Was bald wieder dran ist — ohne das, was schon offen auf der Liste steht,
 * und ohne Ausgeblendetes. `hidden` merkt sich je Schlüssel den Kauftag, an
 * dem ausgeblendet wurde: Ein neuer Kauf hebt das Ausblenden auf.
 */
export function consumptionSuggestions(
  rows: readonly PurchaseRow[],
  options: {
    today: IsoDate;
    timeZone: string;
    hidden?: Readonly<Record<string, IsoDate>>;
  },
): ConsumptionSuggestion[] {
  const onList = new Set(
    rows.filter((row) => !row.deletedAt && !row.checkedAt).map((row) => itemKey(row.name)),
  );
  const bought = new Map<string, { days: IsoDate[]; latest: PurchaseRow; latestAt: string }>();
  for (const row of rows) {
    if (!row.checkedAt) continue;
    const key = itemKey(row.name);
    if (!key) continue;
    const day = isoDateInZone(new Date(row.checkedAt), options.timeZone);
    const entry = bought.get(key);
    if (!entry) {
      bought.set(key, { days: [day], latest: row, latestAt: row.checkedAt });
    } else {
      entry.days.push(day);
      if (row.checkedAt > entry.latestAt) {
        entry.latest = row;
        entry.latestAt = row.checkedAt;
      }
    }
  }

  const out: ConsumptionSuggestion[] = [];
  for (const [key, entry] of bought) {
    if (onList.has(key)) continue;
    const pattern = consumptionPattern(entry.days, options.today);
    if (pattern.status !== 'pattern' || !pattern.due) continue;
    if (options.hidden?.[key] === pattern.lastBought) continue;
    out.push({ key, name: entry.latest.name.trim(), category: entry.latest.category, pattern });
  }
  // Am längsten überfällig zuerst (relativ zum eigenen Intervall).
  return out.sort(
    (a, b) =>
      b.pattern.daysSince! / b.pattern.intervalDays! -
        a.pattern.daysSince! / a.pattern.intervalDays! || a.name.localeCompare(b.name, 'de'),
  );
}
